import React, { useState } from 'react';
import {
    Box, Paper, Typography, Grid, TextField, Button, FormControl,
    Select, MenuItem, Slider, Chip, IconButton, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useTheme, alpha, Divider, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { faker } from '@faker-js/faker';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const fieldTypes = [
    { value: 'firstName', label: 'First Name', category: 'Person' },
    { value: 'lastName', label: 'Last Name', category: 'Person' },
    { value: 'fullName', label: 'Full Name', category: 'Person' },
    { value: 'email', label: 'Email', category: 'Person' },
    { value: 'phone', label: 'Phone', category: 'Person' },
    { value: 'avatar', label: 'Avatar URL', category: 'Person' },
    { value: 'jobTitle', label: 'Job Title', category: 'Person' },
    { value: 'company', label: 'Company', category: 'Company' },
    { value: 'streetAddress', label: 'Street Address', category: 'Address' },
    { value: 'city', label: 'City', category: 'Address' },
    { value: 'state', label: 'State', category: 'Address' },
    { value: 'zipCode', label: 'Zip Code', category: 'Address' },
    { value: 'country', label: 'Country', category: 'Address' },
    { value: 'uuid', label: 'UUID', category: 'ID' },
    { value: 'number', label: 'Random Number', category: 'Number' },
    { value: 'date', label: 'Date', category: 'Date' },
    { value: 'pastDate', label: 'Past Date', category: 'Date' },
    { value: 'futureDate', label: 'Future Date', category: 'Date' },
    { value: 'boolean', label: 'Boolean', category: 'Other' },
    { value: 'word', label: 'Random Word', category: 'Text' },
    { value: 'sentence', label: 'Sentence', category: 'Text' },
    { value: 'paragraph', label: 'Paragraph', category: 'Text' },
    { value: 'url', label: 'URL', category: 'Internet' },
    { value: 'ipv4', label: 'IPv4 Address', category: 'Internet' },
    { value: 'color', label: 'Color (HEX)', category: 'Other' },
    { value: 'price', label: 'Price', category: 'Commerce' },
    { value: 'product', label: 'Product Name', category: 'Commerce' },
];

const presetSchemas = [
    {
        name: 'Users',
        fields: [
            { name: 'id', type: 'uuid' },
            { name: 'firstName', type: 'firstName' },
            { name: 'lastName', type: 'lastName' },
            { name: 'email', type: 'email' },
            { name: 'phone', type: 'phone' },
        ]
    },
    {
        name: 'Products',
        fields: [
            { name: 'id', type: 'uuid' },
            { name: 'name', type: 'product' },
            { name: 'price', type: 'price' },
            { name: 'inStock', type: 'boolean' },
        ]
    },
    {
        name: 'Addresses',
        fields: [
            { name: 'street', type: 'streetAddress' },
            { name: 'city', type: 'city' },
            { name: 'state', type: 'state' },
            { name: 'zip', type: 'zipCode' },
            { name: 'country', type: 'country' },
        ]
    },
];

const generateValue = (type) => {
    switch (type) {
        case 'firstName': return faker.person.firstName();
        case 'lastName': return faker.person.lastName();
        case 'fullName': return faker.person.fullName();
        case 'email': return faker.internet.email();
        case 'phone': return faker.phone.number();
        case 'avatar': return faker.image.avatar();
        case 'jobTitle': return faker.person.jobTitle();
        case 'company': return faker.company.name();
        case 'streetAddress': return faker.location.streetAddress();
        case 'city': return faker.location.city();
        case 'state': return faker.location.state();
        case 'zipCode': return faker.location.zipCode();
        case 'country': return faker.location.country();
        case 'uuid': return faker.string.uuid();
        case 'number': return faker.number.int({ min: 1, max: 1000 });
        case 'date': return faker.date.recent().toISOString().split('T')[0];
        case 'pastDate': return faker.date.past().toISOString().split('T')[0];
        case 'futureDate': return faker.date.future().toISOString().split('T')[0];
        case 'boolean': return faker.datatype.boolean();
        case 'word': return faker.lorem.word();
        case 'sentence': return faker.lorem.sentence();
        case 'paragraph': return faker.lorem.paragraph();
        case 'url': return faker.internet.url();
        case 'ipv4': return faker.internet.ipv4();
        case 'color': return faker.internet.color();
        case 'price': return parseFloat(faker.commerce.price());
        case 'product': return faker.commerce.productName();
        default: return '';
    }
};

const MockDataGenerator = () => {
    const theme = useTheme();
    const [fields, setFields] = useState([
        { name: 'id', type: 'uuid' },
        { name: 'name', type: 'fullName' },
        { name: 'email', type: 'email' },
        { name: 'city', type: 'city' },
    ]);
    const [rowCount, setRowCount] = useState(10);
    const [outputFormat, setOutputFormat] = useState('json');
    const [data, setData] = useState([]);
    const [copied, setCopied] = useState(false);

    const generateData = () => {
        const newData = [];
        for (let i = 0; i < rowCount; i++) {
            const row = {};
            fields.forEach((field) => {
                row[field.name] = generateValue(field.type);
            });
            newData.push(row);
        }
        setData(newData);
    };

    const addField = () => {
        setFields([...fields, { name: `field${fields.length + 1}`, type: 'word' }]);
    };

    const removeField = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    const updateField = (index, key, value) => {
        const newFields = [...fields];
        newFields[index][key] = value;
        setFields(newFields);
    };

    const loadPreset = (preset) => {
        setFields([...preset.fields]);
        setData([]);
    };

    const getOutput = () => {
        if (outputFormat === 'json') {
            return JSON.stringify(data, null, 2);
        } else {
            // CSV
            if (data.length === 0) return '';
            const headers = Object.keys(data[0]).join(',');
            const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(',')).join('\n');
            return `${headers}\n${rows}`;
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(getOutput());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = () => {
        const blob = new Blob([getOutput()], { type: outputFormat === 'json' ? 'application/json' : 'text/csv' });
        const link = document.createElement('a');
        link.download = `mock-data.${outputFormat}`;
        link.href = URL.createObjectURL(blob);
        link.click();
    };

    return (
        <>
            <SEO
                title="Mock Data Generator | Fake JSON/CSV Maker | UmbrellaCo"
                description="Generate realistic mock data for testing and development. Create custom schemas and export fake data in JSON or CSV format."
                keywords="mock data generator, fake data maker, test data generator, json generator, csv mock data"
                url="/dev-tools/mock-data"
                jsonLd={createSoftwareAppSchema(
                    'Mock Data Generator',
                    'Generate custom realistic mock data for testing and development.',
                    'https://umbrellaco.in/dev-tools/mock-data',
                    ['Data Generation', 'Testing Tools', 'Developer Utilities']
                )}
            />
            <ToolPageContainer
                title="Mock Data Generator"
                description="Generate realistic fake data for testing and development. Build custom schemas using presets and export to JSON or CSV."
                disablePadding
            >
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
            <Grid container spacing={3}>
                {/* Left Panel - Schema Builder */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>Presets</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {presetSchemas.map((preset) => (
                                <Chip
                                    key={preset.name}
                                    label={preset.name}
                                    onClick={() => loadPreset(preset)}
                                    variant="outlined"
                                    size="small"
                                />
                            ))}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 2 }}>Schema Fields</Typography>
                        {fields.map((field, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    placeholder="Field name"
                                    value={field.name}
                                    onChange={(e) => updateField(index, 'name', e.target.value)}
                                    sx={{ flex: 1 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select
                                        value={field.type}
                                        onChange={(e) => updateField(index, 'type', e.target.value)}
                                    >
                                        {fieldTypes.map((ft) => (
                                            <MenuItem key={ft.value} value={ft.value}>
                                                {ft.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton size="small" onClick={() => removeField(index)} color="error">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}

                        <Button
                            startIcon={<AddIcon />}
                            onClick={addField}
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            Add Field
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Number of rows: {rowCount}
                            </Typography>
                            <Slider
                                value={rowCount}
                                onChange={(e, v) => setRowCount(v)}
                                min={1}
                                max={100}
                                step={1}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <ToggleButtonGroup
                                value={outputFormat}
                                exclusive
                                onChange={(e, v) => v && setOutputFormat(v)}
                                size="small"
                            >
                                <ToggleButton value="json">JSON</ToggleButton>
                                <ToggleButton value="csv">CSV</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<RefreshIcon />}
                            onClick={generateData}
                            sx={{ mt: 2 }}
                        >
                            Generate Data
                        </Button>
                    </Paper>
                </Grid>

                {/* Right Panel - Output */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2">
                                Output ({data.length} rows)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                                    <IconButton size="small" onClick={handleCopy} disabled={data.length === 0}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                    <IconButton size="small" onClick={handleDownload} disabled={data.length === 0}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {data.length > 0 ? (
                            <>
                                {/* Preview Table */}
                                <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {fields.map((field) => (
                                                    <TableCell key={field.name} sx={{ fontWeight: 600 }}>
                                                        {field.name}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.slice(0, 5).map((row, idx) => (
                                                <TableRow key={idx}>
                                                    {fields.map((field) => (
                                                        <TableCell key={field.name} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {String(row[field.name])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {data.length > 5 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                        Showing 5 of {data.length} rows...
                                    </Typography>
                                )}

                                {/* Raw Output */}
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: alpha(theme.palette.common.black, 0.03),
                                        borderRadius: 1,
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'pre-wrap',
                                        maxHeight: 200,
                                        overflow: 'auto'
                                    }}
                                >
                                    {getOutput().substring(0, 2000)}
                                    {getOutput().length > 2000 && '...'}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{
                                py: 8,
                                textAlign: 'center',
                                color: 'text.secondary',
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                                borderRadius: 1
                            }}>
                                <Typography>Configure your schema and click "Generate Data"</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            </Box>
            </ToolPageContainer>
        </>
    );
};

export default MockDataGenerator;
